import { startJourney, takeNextJourneyStep } from "@flow/common/core_routes";
import { Consent, JourneyContext, JourneyGuid, PublicJourneyStep, PublicJourneyStepResponse } from "@flow/common/types";
import { Text } from "@flow/lib/i18n";
import { Box, Grid, Hidden, Paper, useMediaQuery } from "@mui/material";
import Typography from "@mui/material/Typography";
import { usePostHog } from "posthog-js/react";
import React from "react";
import { getActionTypeHandler } from "../action_registry";
import { memberTheme } from "../memberTheme";
import { RootDispatch } from "../plugin_api";
import { useUserAnalyticsService } from "../user_analytics_service";
import { ActionComponentWrapper } from "./ActionComponentWrapper";
import { JourneyContinuation } from "./JourneyContinuation";

/**
 * `MemberJourney` is the top level React component for member journey components.
 * It manages journey progression through a series of steps in the member journey, fetching and displaying
 * relevant content and actions as the user progresses. The component also handles internationalization
 * by loading translations for different languages.
 *
 * @param props - The component props:
 *   - `journey_guid`: A unique identifier for the member's journey.
 *   - `initialContext`: The initial context for the member's journey.
 *
 * @returns A `MemberJourney` React component.
 */
const MemberJourney = (props: {
  journey_guid: JourneyGuid;
  initialContext: JourneyContext;
  setIsEarlyAccessStaffUser: (_: boolean) => void;
}) => {
  const posthog = usePostHog();
  const { captureUserAction, capturePageView } = useUserAnalyticsService();
  const [notFound, setNotFound] = React.useState(false);
  const [journeyEnded, setJourneyEnded] = React.useState(false);
  const [ctx, setCtx] = React.useState<JourneyContext>(props.initialContext);
  const [currentStep, setCurrentStep] = React.useState<PublicJourneyStep | undefined>(undefined);
  const [journeyName, setJourneyName] = React.useState<string>("");
  const [backgroundImageUrl, setBackgroundImageUrl] = React.useState<string | undefined>(undefined);
  const [backgroundColourHex, setBackgroundColourHex] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (ctx.member.external_id !== undefined) {
      // identify the Flow user to posthog so their behaviour can be tracked against this id.
      // this op will interact with posthog infrastructure and trigger network calls
      // this is non-blocking (fire-and-forget)
      posthog?.identify(ctx.member.external_id);
    }
    // set Journey guid value to be sent with every user event to posthog
    // this op should not trigger any network calls to posthog infrastructure, it only interacts with local storage
    // this is non-blocking (fire-and-forget)
    posthog?.register_for_session({ journeyGuid: props.journey_guid });
  }, [posthog, ctx.member.external_id, props.journey_guid]);

  function _setStep(r: PublicJourneyStepResponse) {
    setJourneyName(r.journey_name);
    if (r.type === "journey_ended") {
      setJourneyEnded(true);
      setCurrentStep(undefined);
    } else {
      const actionHandler = getActionTypeHandler(r.step.action);

      if (actionHandler === undefined) {
        // action type not found. set the data anyway, and let the template complain
        setCurrentStep({
          action: r.step.action,
          variant_idx: r.step.variant_idx,
          custom_data: {},
          redacted_prefill: undefined,
          consents: [],
          isStaff: false,
          takenAction: r.step.takenAction,
          sam_prefill: r.step.sam_prefill,
          show_journey_title: r.step.show_journey_title,
        });
      } else {
        // validate that custom data matches expected schema for this
        // action handler
        const custom_data = actionHandler.def.custom_data_type.read(r.step.custom_data);

        props.setIsEarlyAccessStaffUser(r.isEarlyAccessStaffUser);

        setCurrentStep({
          action: r.step.action,
          variant_idx: r.step.variant_idx,
          redacted_prefill: r.step.redacted_prefill,
          consents: r.step.consents,
          custom_data,
          isStaff: r.step.isStaff,
          takenAction: r.step.takenAction,
          sam_prefill: r.step.sam_prefill,
          show_journey_title: r.step.show_journey_title,
        });

        if (r.step.isStaff) {
          // paranoid try/catch to help limit the impact of bad Posthog operations on Flow Journey logic.
          try {
            // intentionally leaving first arg undefined, see Posthog function type def.
            // is_staff should be set once and is intended not to be overridden.
            posthog?.setPersonProperties(undefined, { is_staff: true });
          } catch (e) {
            console.warn("Failed to identify staff member to Posthog", e);
          }
        }
      }
    }
  }

  const dispatch: RootDispatch = {
    setBackground: (imageUrl?: string, colourHex?: string) => {
      setBackgroundImageUrl(imageUrl);
      setBackgroundColourHex(colourHex);
    },
    gotoNextStep: (
      actionStatus: "skipped" | "completed",
      updatedContext: JourneyContext,
      consents: Consent[]
    ): void => {
      // reset window scroll position for new step
      window.scrollTo(0, 0);
      // remove background image for next step
      setBackgroundImageUrl(undefined);
      setBackgroundColourHex(undefined);
      if (currentStep === undefined) {
        startJourney
          .call({ journey_guid: props.journey_guid, context: props.initialContext })
          .then(_setStep)
          .catch((e) => setNotFound(true));
      } else {
        setCtx(updatedContext);
        const currentStepCopy = { ...currentStep };
        // call flow backend
        takeNextJourneyStep
          .call({
            performed_action: actionStatus === "completed",
            journey_guid: props.journey_guid,
            from_action_guid: currentStep.action.guid,
            context: updatedContext,
            consents: consents,
          })
          .then(_setStep)
          .finally(() => {
            // record page view as we move to the next journey step
            capturePageView({
              actionGuid: currentStepCopy.action.guid,
            });
            // record user taking action, whether it was skipped or completed
            captureUserAction({
              actionType: currentStepCopy.action.type,
              actionStatus: actionStatus,
            });
          });
      }
    },
  };

  // things run when component is first mounted
  React.useEffect(() => {
    // load initial step
    dispatch.gotoNextStep("completed", props.initialContext, []);
  }, []);

  return (
    <>
      {/* If an action sets a background colour then apply it as a background but below image should still take precedence on non-mobile screens */}
      {backgroundColourHex && <Background backgroundColour={backgroundColourHex} />}

      {/* If an action sets an image then on non-mobile screens this will override background colour setting */}
      {useMediaQuery(memberTheme.breakpoints.up("sm")) && backgroundImageUrl && (
        <Background imageUrl={backgroundImageUrl} />
      )}

      <Grid container>
        <Grid item md={1} implementation="css" xsDown component={Hidden} />
        <Grid item md={10} xs={12}>
          {/* // the paddingLeft prop below on Typography is a bit of a hack to align lhs with ActionComponent containers
          rendered below // the Grid here is to align journey title with actions using `ActionComponentWrapper` rendered
          beneath. */}
          <Grid
            container
            justifyContent={"flex-start"}
            columns={10}
            sx={{
              marginTop: "0",
              marginY: "1.5rem",
              paddingLeft: "0.5rem",
            }}
          >
            {!journeyEnded && (
              <Grid item sx={{ padding: "0px !important" }} xs={10} md={7}>
                {(() => {
                  const isLargeScreenWithBackground =
                    useMediaQuery(memberTheme.breakpoints.up("sm")) && backgroundImageUrl;

                  if (currentStep?.show_journey_title) {
                    // Case 1 & Case 3: show_journey_title is true
                    if (isLargeScreenWithBackground) {
                      return (
                        <Box
                          sx={{
                            padding: "16px 16px",
                            backgroundColor: "rgba(255, 255, 255, 0.60)",
                            borderRadius: "12px",
                            marginRight: { md: "10px" },
                          }}
                        >
                          <Typography sx={{ typography: { xs: "h5", md: "h4" } }}>{journeyName}</Typography>
                        </Box>
                      );
                    }
                    return <Typography sx={{ typography: { xs: "h5", md: "h4" } }}>{journeyName}</Typography>;
                  }

                  if (currentStep?.action.type === "petition:PetitionAction") {
                    // Case 2 & Case 4: show_journey_title is false and action.type === "petition:PetitionAction"
                    if (isLargeScreenWithBackground) {
                      return (
                        <Box
                          sx={{
                            padding: "16px 16px",
                            backgroundColor: "rgba(255, 255, 255, 0.60)",
                            borderRadius: "12px",
                            marginRight: { md: "10px" },
                          }}
                        >
                          <Typography sx={{ typography: { xs: "h5", md: "h4" } }}>
                            {currentStep?.custom_data.title}
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Typography sx={{ typography: { xs: "h5", md: "h4" } }}>
                        {currentStep?.custom_data.title}
                      </Typography>
                    );
                  }

                  return null;
                })()}
              </Grid>
            )}
          </Grid>
          {notFound && (
            <div>
              <Text id="page_not_found">Not found</Text>
            </div>
          )}
          {journeyEnded && ctx.member.email && (
            <JourneyContinuation email={ctx.member.email} journey_name={journeyName} />
          )}
          {journeyEnded && !ctx.member.email && (
            <ActionComponentWrapper>
              <Text id="core.journey_ended">
                There are no more actions in this campaign. If you are the campaign starter consider adding a redirect
                at the end of the journey.
              </Text>
            </ActionComponentWrapper>
          )}
          {currentStep && (
            <ActionComponent
              key={currentStep.action.guid}
              journey_guid={props.journey_guid}
              step={currentStep}
              context={ctx}
              dispatch={dispatch}
            />
          )}
          {!notFound && !journeyEnded && !currentStep && (
            <Text id="page_loading">
              Loading...please bear with us - lots of people are taking action right now so the page might take a bit
              longer to appear!
            </Text>
          )}
        </Grid>
        <Grid item md={1} implementation="css" xsDown component={Hidden} />
      </Grid>
    </>
  );
};

const ActionComponent = (props: {
  journey_guid: JourneyGuid;
  step: PublicJourneyStep;
  context: JourneyContext;
  dispatch: RootDispatch;
}) => {
  const actionHandler = getActionTypeHandler(props.step.action);

  return (
    <>
      {actionHandler === undefined ? (
        <div>Something went wrong. No {props.step.action.type} action found.</div>
      ) : (
        React.createElement(actionHandler.component, {
          action: props.step.action,
          variant_idx: props.step.variant_idx,
          ctx: props.context,
          dispatch: props.dispatch,
          custom: props.step.custom_data,
          redacted_prefill: props.step.redacted_prefill,
          consents: props.step.consents,
          isStaff: props.step.isStaff,
          takenAction: props.step.takenAction,
          sam_prefill: props.step.sam_prefill,
          show_journey_title: props.step.show_journey_title,
        })
      )}
    </>
  );
};

function Background(props: { imageUrl?: string; backgroundColour?: string }) {
  return (
    <Paper
      sx={{
        backgroundImage: props.imageUrl ? `url(${props.imageUrl})` : "none",
        backgroundColor: props.backgroundColour || "transparent",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        position: "fixed",
        top: 0,
        left: 0,
        height: "100%",
        width: "100%",
        zIndex: -100,
        borderRadius: 0,
      }}
    />
  );
}

export { MemberJourney };
